import { IonContent, IonModal } from '@ionic/react';
import { setTag } from '@sentry/capacitor';
import { useEffect, useMemo, useRef, useState, type ComponentProps, type PropsWithChildren, type Ref } from 'react';
import { createPortal } from 'react-dom';

import { fizzySearchTags } from '@/utils/fizzySearch';
import { ITag } from '@/utils/getFirebaseTag';

import { useDatabase } from '@/hooks/useDatabase';
import { useFirebaseTags } from '@/hooks/useFirebaseTags';
import { useScrollToInputWhenKeyboardOpens } from '@/hooks/useScrollToInputWhenKeyboardOpens';

import { useUserInfoContext } from '@/contexts/userInfoContext';

import styles from './TagModal.module.css';

import { Button } from '../Button';
import { Divider } from '../Divider/Divider';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { ModalFooter } from '../ModalFooter/ModalFooter';
import { ModalHeader } from '../ModalHeader/ModalHeader';
import { Tag } from '../Tag/Tag';

interface ModalProps extends ComponentProps<typeof IonModal> {
  ref?: Ref<HTMLIonModalElement>;
  handleCancel: VoidFunction;
  handleClose: (tags?: ITag[]) => void;
  initialValue?: ITag[];
}

const normalizeLabel = (value?: string) =>
  (value ?? '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-width chars
    .trim();

const sanitizeITags = (tags: ITag[]) => {
  const seen = new Set<string>();
  return (tags ?? [])
    .map((t) => ({ ...t, label: normalizeLabel(t.label) }))
    .filter((t) => t.label.length > 0) // remove empty labels
    .filter((t) => {
      const key = t.label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const TagModal = ({ handleClose, initialValue, handleCancel, ref, ...props }: PropsWithChildren<ModalProps>) => {
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 594 });
  const [localTags, setLocalTags] = useState<ITag[]>(sanitizeITags(initialValue ?? []));
  const { userInfo } = useUserInfoContext();
  const { createTagInFirebase } = useDatabase();

  const { tags: firebaseTags, refetch } = useFirebaseTags(userInfo.organization_id, userInfo.id);

  useEffect(() => {
    setLocalTags(sanitizeITags(initialValue ?? []));
  }, [initialValue]);

  useScrollToInputWhenKeyboardOpens();

  const filteredTags = useMemo(() => {
    return fizzySearchTags(searchTerm, firebaseTags);
  }, [firebaseTags, searchTerm]);

  const handleTagSelect = (tag: string) => {
    // remove tag if it already exists
    if (localTags.find((t) => t.label === tag)) {
      setSearchTerm('');
      return;
    }

    const cleaned = normalizeLabel(tag);
    if (!cleaned) {
      setSearchTerm('');
      return;
    }

    setLocalTags(
      sanitizeITags([
        ...localTags,
        {
          id: '',
          label: cleaned,
          user: userInfo.id,
        },
      ]),
    );
    setSearchTerm('');
  };

  useEffect(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();

      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [searchTerm, localTags]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      setLocalTags(initialValue ?? []);
      setSearchTerm('');
    };
  }, []);

  /**
   * Handles the closing of a tag modal by processing multiple tags.
   * For each tag, it checks if the tag exists. If it does, it uses the tag.
   * If a tag does not exist, it creates a new tag in Firebase.
   * Finally, it triggers the close handler with the IDs of all processed tags.
   *
   * @param {string[]} [tagsToProcess] - Array of tag labels to be processed.
   */
  async function wrappedCloseHandler(tagsToProcess: ITag[]) {
    const cleanedTags = sanitizeITags(tagsToProcess);
    const processedTags: ITag[] = [];

    for (const tag of cleanedTags) {
      const existingTag = firebaseTags.find(
        (dbTag) => normalizeLabel(dbTag.label).toLowerCase() === tag.label.toLowerCase(),
      );

      if (existingTag) {
        processedTags.push(existingTag);
      } else {
        // Extra safeguard: never create an empty tag
        if (!tag.label) continue;

        const newTag = await createTagInFirebase(tag.label, userInfo.organization_id, userInfo.id);
        if (newTag && normalizeLabel(newTag.label)) {
          processedTags.push({ ...newTag, label: normalizeLabel(newTag.label) });
        }
      }
    }
    await refetch();
    return handleClose(processedTags);
  }

  const handleLocalTagRemove = (tag: ITag) => {
    const filteredTags = localTags.filter((t) => t.label !== tag.label);

    setLocalTags(filteredTags);
  };

  const cleanUp = () => {
    setSearchTerm('');
    setLocalTags(initialValue ?? []);
    handleCancel();
  };

  return (
    <>
      <IonModal className={styles.modal} {...props} onDidDismiss={cleanUp} ref={ref}>
        <ModalHeader>Tags</ModalHeader>
        <IonContent className={styles.content}>
          <div className={styles.inner}>
            <ul className={styles.tags}>
              {localTags.map((tag, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <li className={styles.tag} key={`tag-${tag.id}=${idx}`}>
                  <Tag onClick={() => handleLocalTagRemove(tag)} variant='measurement'>
                    {tag.label}
                    <Icon className={styles.tagIcon} name='cross' />
                  </Tag>
                </li>
              ))}
            </ul>

            <div className={styles.input} ref={inputRef}>
              <Input
                isSearch
                leadingIcon='search'
                onIonInput={(event) => {
                  setSearchTerm(event.detail.value ?? '');
                }}
                placeholder='Find or create a tag...'
                trailingIcon={searchTerm ? 'cross-circle' : undefined}
                value={searchTerm}
                variant='primary'
              />
            </div>
          </div>
        </IonContent>
        <ModalFooter>
          {' '}
          <>
            <Button className={styles.cancelButton} onClick={cleanUp} size='small' variant='secondary'>
              Cancel
            </Button>
            <div className={styles.buttons}>
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setLocalTags([]);
                }}
                size='small'
                variant='secondary'>
                Clear all tags
              </Button>

              <Button onClick={() => wrappedCloseHandler(localTags)} size='small' variant='primary'>
                Save
              </Button>
            </div>
          </>
        </ModalFooter>
      </IonModal>

      {searchTerm &&
        createPortal(
          <div
            className={styles.dropdown}
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: '216px', // 5 entries + the add tag option = 6 x 36?
              zIndex: 9999,
            }}>
            {filteredTags.length > 0 &&
              filteredTags.map((tag) => (
                <div key={tag.id} style={{ width: '100%' }}>
                  <div className={styles.dropdownItem} onClick={() => handleTagSelect(tag.label)}>
                    <Tag variant='measurement'>{tag.label}</Tag>
                  </div>
                  <Divider />
                </div>
              ))}
            <div className={styles.dropdownItem} onClick={() => handleTagSelect(searchTerm)}>
              <Icon name='plus' size={14} />
              <p className={styles.dropdownText}>
                Create tag: <strong>&apos;{searchTerm}&apos;</strong>
              </p>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
